import { useState, useCallback, useRef, useEffect, useMemo } from "react";

const useTeleprompter = ({ song, onConfigChange }) => {
  // Config
  const [scrollSpeed, setScrollSpeed] = useState(120); // WPM
  const [fontSize, setFontSize] = useState(24); // px
  const [countdownSeconds, setCountdownSeconds] = useState(3);
  const [syncConfig, setSyncConfig] = useState(null); // Loaded from song metadata

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentLineIndex, setCurrentLineIndex] = useState(-1);
  const [showSetup, setShowSetup] = useState(false);
  const [showSectionEditor, setShowSectionEditor] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);

  // Refs
  const scrollRef = useRef(null);
  const animFrameRef = useRef(null);
  const startTimeRef = useRef(0);
  const beatWaveSurferRef = useRef(null);
  const songIdRef = useRef(null);
  const isPlayingRef = useRef(false);

  // Parse lyrics into lines
  const lines = song?.content ? song.content.split("\n") : [];

  // Derive sections from syncConfig
  const sections = useMemo(
    () => syncConfig?.sections || [],
    [syncConfig],
  );

  // Load config from song when song changes
  useEffect(() => {
    if (!song) return;
    if (song.id === songIdRef.current) return; // same song, skip
    songIdRef.current = song.id;

    if (song.teleprompterConfig) {
      setSyncConfig(song.teleprompterConfig);
      // Restore global scroll speed if stored
      if (song.teleprompterConfig.scrollSpeed) {
        setScrollSpeed(song.teleprompterConfig.scrollSpeed);
      }
    } else {
      setSyncConfig(null);
    }
  }, [song]);

  // Dynamic beatWaveSurfer binding — called by TeleprompterView after creating the instance
  const setBeatWaveSurfer = useCallback((ws) => {
    beatWaveSurferRef.current = ws;
  }, []);

  // Auto-detect WPM from lyrics word count and audio duration
  const autoDetectWPM = useCallback(
    (duration) => {
      if (!duration || duration <= 0 || !lines.length) return null;
      const totalWords = lines.reduce(
        (sum, l) => sum + l.split(/\s+/).filter(Boolean).length,
        0,
      );
      const wpm = Math.round((totalWords / duration) * 60);
      const clamped = Math.max(30, Math.min(300, wpm));
      setScrollSpeed(clamped);
      return clamped;
    },
    [lines],
  );

  // Save section config and persist to song
  const saveSectionConfig = useCallback(
    (newSections) => {
      const newConfig = {
        ...syncConfig,
        sections: newSections,
        scrollSpeed,
      };
      setSyncConfig(newConfig);
      if (onConfigChange) {
        onConfigChange(newConfig);
      }
    },
    [syncConfig, scrollSpeed, onConfigChange],
  );

  // Manual scrolling engine — supports per-section WPM, skips blank lines
  const startManualScroll = useCallback(() => {
    if (!lines.length) return;

    startTimeRef.current = performance.now();

    // Build per-line WPM map (sections override global)
    const lineWpm = new Array(lines.length).fill(scrollSpeed);
    for (const section of sections) {
      const sectionWpm = section.wpm || scrollSpeed;
      for (let i = section.startLine - 1; i < section.endLine && i < lines.length; i++) {
        lineWpm[i] = sectionWpm;
      }
    }

    // Pre-compute cumulative time for each line
    // Blank lines get 0 duration so they're skipped instantly
    const lineStartTimes = new Array(lines.length);
    lineStartTimes[0] = 0;
    for (let i = 1; i < lines.length; i++) {
      const prevWords = lines[i - 1].split(/\s+/).filter(Boolean).length;
      if (prevWords === 0) {
        lineStartTimes[i] = lineStartTimes[i - 1]; // blank — skip
      } else {
        lineStartTimes[i] = lineStartTimes[i - 1] + (prevWords / lineWpm[i - 1]) * 60;
      }
    }

    const animate = () => {
      const elapsed = (performance.now() - startTimeRef.current) / 1000;

      // Binary search for current line
      let lo = 0;
      let hi = lines.length - 1;
      while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        if (lineStartTimes[mid] <= elapsed) {
          lo = mid;
        } else {
          hi = mid - 1;
        }
      }
      setCurrentLineIndex(lo);

      if (lo < lines.length - 1) {
        animFrameRef.current = requestAnimationFrame(animate);
      } else {
        setIsPlaying(false);
      }
    };

    animFrameRef.current = requestAnimationFrame(animate);
  }, [scrollSpeed, lines, sections]);

  // Audio-synced scrolling (when syncConfig and beatWaveSurfer exist)
  // Uses word-weighted interpolation so blank lines are skipped
  const startSyncedScroll = useCallback(() => {
    const ws = beatWaveSurferRef.current;
    if (!ws || !syncConfig) return;

    // Pre-compute word-weighted timestamps per section
    // so blank lines get 0 audio time and are skipped
    let sectionLineTimes = null;
    if (syncConfig.sections?.length > 0) {
      sectionLineTimes = [];
      for (const section of syncConfig.sections) {
        const audioDur = section.audioEndSec - section.audioStartSec;
        const sectionLines = [];
        let totalWords = 0;
        for (let i = section.startLine - 1; i < section.endLine && i < lines.length; i++) {
          const words = lines[i].split(/\s+/).filter(Boolean).length;
          sectionLines.push({ index: i, words });
          totalWords += words;
        }
        // Distribute audio time proportionally by word count
        let cumTime = section.audioStartSec;
        for (const line of sectionLines) {
          sectionLineTimes.push({ index: line.index, time: cumTime });
          if (totalWords > 0) {
            cumTime += (line.words / totalWords) * audioDur;
          }
        }
      }
      // Sort by time for reverse scan
      sectionLineTimes.sort((a, b) => a.time - b.time);
    }

    const animate = () => {
      const currentTime = ws.getCurrentTime();

      // Check per-line timestamps first
      if (syncConfig.lineTimestamps) {
        const entries = Object.entries(syncConfig.lineTimestamps)
          .map(([line, time]) => [parseInt(line) - 1, time])
          .sort((a, b) => a[1] - b[1]);

        for (let i = entries.length - 1; i >= 0; i--) {
          if (currentTime >= entries[i][1]) {
            setCurrentLineIndex(entries[i][0]);
            break;
          }
        }
      } else if (sectionLineTimes) {
        // Word-weighted section interpolation — blank lines skipped
        for (let i = sectionLineTimes.length - 1; i >= 0; i--) {
          if (currentTime >= sectionLineTimes[i].time) {
            setCurrentLineIndex(sectionLineTimes[i].index);
            break;
          }
        }
      }

      if (isPlayingRef.current) {
        animFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animFrameRef.current = requestAnimationFrame(animate);
  }, [syncConfig, lines]);

  // Start playback
  const startPlayback = useCallback(() => {
    setIsPlaying(true);
    isPlayingRef.current = true;
    const ws = beatWaveSurferRef.current;

    // Always play beat audio when available
    if (ws) {
      try { ws.play(); } catch (e) { /* ignore */ }
    }

    if (syncConfig && ws) {
      startSyncedScroll();
    } else {
      startManualScroll();
    }
  }, [syncConfig, startSyncedScroll, startManualScroll]);

  // Pause playback
  const pausePlayback = useCallback(() => {
    setIsPlaying(false);
    isPlayingRef.current = false;
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    const ws = beatWaveSurferRef.current;
    if (ws) {
      try {
        ws.pause();
      } catch (e) {
        /* ignore */
      }
    }
  }, []);

  // Stop playback
  const stopPlayback = useCallback(() => {
    pausePlayback();
    setCurrentLineIndex(-1);
    const ws = beatWaveSurferRef.current;
    if (ws) {
      try {
        ws.seekTo(0);
      } catch (e) {
        /* ignore */
      }
    }
  }, [pausePlayback]);

  // Handle tab visibility change
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && isPlaying) {
        // Tab hidden — pause animation frame but keep state
        if (animFrameRef.current) {
          cancelAnimationFrame(animFrameRef.current);
          animFrameRef.current = null;
        }
      } else if (!document.hidden && isPlaying) {
        // Tab visible again — resume
        const ws = beatWaveSurferRef.current;
        if (syncConfig && ws) {
          startSyncedScroll();
        } else {
          // For manual mode, adjust startTime to account for hidden duration
          startManualScroll();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [isPlaying, syncConfig, startSyncedScroll, startManualScroll]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, []);

  // Countdown completion handler
  const onCountdownComplete = useCallback(() => {
    setShowCountdown(false);
    startPlayback();
  }, [startPlayback]);

  // Load sync config from song metadata (if exists)
  const loadConfig = useCallback((config) => {
    setSyncConfig(config);
  }, []);

  // Save sync config
  const saveConfig = useCallback((config) => {
    setSyncConfig(config);
  }, []);

  return {
    // State
    isPlaying,
    currentLineIndex,
    scrollSpeed,
    fontSize,
    countdownSeconds,
    syncConfig,
    sections,
    showSetup,
    showSectionEditor,
    showCountdown,
    scrollRef,
    lines,

    // Actions
    startPlayback,
    pausePlayback,
    stopPlayback,
    setScrollSpeed,
    setFontSize,
    setCountdownSeconds,
    setShowSetup,
    setShowSectionEditor,
    setShowCountdown,
    loadConfig,
    saveConfig,
    saveSectionConfig,
    onCountdownComplete,
    setCurrentLineIndex,
    setBeatWaveSurfer,
    autoDetectWPM,
  };
};

export default useTeleprompter;
